import { useBehavior, useButton, useTriggerAction } from '@libs/core';
import { TButtonData, TWidgetProps } from '@libs/types';
import React from 'react';
import { TButtonOption } from '../../types/options';
import { ButtonRowView } from './ButtonRowView';
import { getDesignHierarchy } from './styles/designHierarchy';

/**
 * Standalone ButtonRow widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useButton) and delegates all rendering to ButtonRowView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional style overrides are required. For composable usages where behavior
 * state is managed externally, use ButtonRowView directly.
 */
export const ButtonRowWidget = ({ widget }: TWidgetProps<TButtonData, TButtonOption>) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const options = widget.options ?? {};
    const data = widget.data ?? {};
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];

    const hierarchy = getDesignHierarchy(options.hierarchy, widget.version);

    return (
        <ButtonRowView
            widget={widget}
            uid={widget.uid}
            label={data.messages?.label ?? data.label}
            parentStyleNames={parentStyleNames}
            isDisabled={isDisabled}
            isHide={isHidden}
            iconName={options.iconName}
            iconVariant={options.iconVariant}
            iconHierarchy={options.iconHierarchy}
            size={options.size}
            alignment={options.alignment}
            hierarchy={hierarchy}
            margin={options.margin}
            onClick={handleClick}
            version={widget.version}
        />
    );
};
